<template>
  <div id="digitalization-projects-list">
    <div id="title-panel">
      <div id="title-container">
        <h1>{{ $t('digitalizationProjectsList.digitalizationProjects') }}</h1>
      </div>

      <p id="description">
        {{ $t('digitalizationProjectsList.description') }}
      </p>

      <vf-button
        v-if="isUserRole([Role.Admin])"
        id="action-button"
        :variant="ButtonVariant.SECONDARY"
        :size="ButtonSize.LARGE"
        @click="handleCreateDigitalizationProjectClick()"
        >{{ $t('digitalizationProjectsList.addDigitalizationProject') }}
      </vf-button>
    </div>

    <table-wrapper
      v-model:page-options="pageOptions"
      v-model:filters="filters"
      :fields="tableFields"
      :filter-configuration="filterConfiguration"
      :items="items"
      :loading="isLoading"
      :mode="TableMode.LAZY"
      row-clickable
      @row-click="handleRowClick"
    ></table-wrapper>
  </div>
</template>

<script lang="ts" setup>
  import {
    TableField,
    TableFieldsBuilder,
    TableFiltersBuilder,
    TableMode,
    useTableWrapper,
  } from '@/components/TableWrapper';
  import {
    ButtonSize,
    ButtonVariant,
  } from '@/components/VfButton/VfButton.types';
  import VfButton from '@/components/VfButton/VfButton.vue';
  import { useToast } from '@/composables/useToast';
  import endpointsList from '@/helpers/endpointsList';
  import { Route, routeParams } from '@/routes';
  import axios, { AxiosResponse } from 'axios';
  import {
    DigitalizationProjectStatus,
    IDigitalizationProjectsPageOptionsDto,
    IDigitalizationProjectSummaryDto,
    IPageDto,
    Role,
  } from 'types';
  import { computed, ref } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useRouter } from 'vue-router';

  const router = useRouter();
  const { t } = useI18n();
  const { createErrorToast } = useToast();

  const items = ref<IDigitalizationProjectSummaryDto[]>([]);
  const tableFields = computed<TableField<IDigitalizationProjectSummaryDto>[]>(
    () =>
      new TableFieldsBuilder<IDigitalizationProjectSummaryDto>({
        sortable: true,
        labelTranslationKey: 'digitalizationProjectsList',
      })
        .addField('title', {
          width: '50%',
        })
        .addField('number', {
          width: '35%',
        })
        .addField('status', {
          valueTranslationKey: 'digitalizationProjectsList.statuses',
          width: '15%',
        })
        .build(),
  );
  const filterConfiguration =
    new TableFiltersBuilder<IDigitalizationProjectSummaryDto>({
      translationKey: 'digitalizationProjectsList',
    })
      .addInputFilter('title', {
        width: '280px',
      })
      .addInputFilter('number', {
        width: '208px',
        translationKey: 'digitalizationProjectsList',
      })
      .addSelectFilter('status', {
        width: '120px',
        translationKey: 'digitalizationProjectsList.statuses',
        selectOptions: Object.values(DigitalizationProjectStatus),
      })
      .build();

  const { filters, filterParams, pageOptions, isLoading } = useTableWrapper<
    IDigitalizationProjectSummaryDto,
    IDigitalizationProjectsPageOptionsDto
  >(TableMode.LAZY, 'title', getDigitalizationProjects);
  async function getDigitalizationProjects() {
    try {
      const response: AxiosResponse<
        IPageDto<IDigitalizationProjectSummaryDto>
      > = await axios.get(
        endpointsList.digitalizationProjects.getDigitalizationProjectsList({
          order: pageOptions.value.sortOrder,
          page: pageOptions.value.page,
          sortBy: pageOptions.value.sortBy,
          take: pageOptions.value.perPage,
          ...filterParams.value,
        }),
      );
      const pageDto = response.data;
      items.value = pageDto.data;
      pageOptions.value.rows = pageDto.meta.itemCount;
    } catch {
      createErrorToast(
        t(
          'digitalizationProjectsList.errors.errorGettingDigitalizationProjects.long',
        ),
      );
    }
  }

  function handleRowClick(item: IDigitalizationProjectSummaryDto): void {
    router.push({
      name: Route.DIGITALIZATION_PROJECT_DETAILS,
      params: {
        [routeParams[Route.DIGITALIZATION_PROJECT_DETAILS].projectId]:
          item._id.toString(),
      },
    });
  }

  function handleCreateDigitalizationProjectClick() {
    router.push({ name: Route.CREATE_DIGITALIZATION_PROJECT });
  }
</script>

<style scoped lang="scss">
  #digitalization-projects-list {
    @include title-panel;
  }
</style>
